<template>
  <div class="business-change-application">
    <div class="header">
      <h2>工商变更申请</h2>
      <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新建申请</el-button>
    </div>

    <!-- 搜索筛选 -->
    <el-card class="search-card">
      <el-form :model="searchForm" inline>
        <el-form-item label="企业/编号">
          <el-input v-model="searchForm.keyword" placeholder="搜索企业名称/申请编号" clearable @clear="handleSearch"
            @keyup.enter.native="handleSearch"></el-input>
        </el-form-item>
        <el-form-item label="变更类型">
          <el-select v-model="searchForm.changeType" placeholder="请选择变更类型" clearable>
            <el-option label="企业名称变更" value="NAME_CHANGE"></el-option>
            <el-option label="法人变更" value="LEGAL_REPRESENTATIVE_CHANGE"></el-option>
            <el-option label="注册资本变更" value="REGISTERED_CAPITAL_CHANGE"></el-option>
            <el-option label="经营范围变更" value="BUSINESS_SCOPE_CHANGE"></el-option>
            <el-option label="地址变更" value="ADDRESS_CHANGE"></el-option>
            <el-option label="其他变更" value="OTHER"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="申请状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="待审核" value="PENDING"></el-option>
            <el-option label="已批准" value="APPROVED"></el-option>
            <el-option label="已拒绝" value="REJECTED"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 申请列表 -->
    <el-table :data="applications" v-loading="loading" border style="width: 100%">
      <el-table-column prop="applicationNumber" label="申请编号" width="160"></el-table-column>
      <el-table-column prop="enterpriseName" label="企业名称" min-width="200"></el-table-column>
      <el-table-column prop="changeType" label="变更类型" width="140">
        <template slot-scope="scope">
          {{ getChangeTypeText(scope.row.changeType) }}
        </template>
      </el-table-column>
      <el-table-column prop="reason" label="申请原因" min-width="200" show-overflow-tooltip></el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)">
            {{ getStatusText(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="submitDate" label="提交时间" width="160">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.submitDate) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" @click="handleView(scope.row)">查看</el-button>
          <el-button v-if="scope.row.status === 'PENDING'" size="mini" type="primary"
            @click="handleEdit(scope.row)">编辑</el-button>
          <el-button v-if="canApprove && scope.row.status === 'PENDING'" size="mini" type="success"
            @click="handleApprove(scope.row)">审批</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="pagination.page"
      :page-sizes="[10, 20, 50, 100]" :page-size="pagination.size" layout="total, sizes, prev, pager, next, jumper"
      :total="pagination.total" style="margin-top: 20px; text-align: right"></el-pagination>

    <!-- 新建/编辑申请对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="800px" @close="handleDialogClose">
      <el-form :model="applicationForm" :rules="rules" ref="applicationForm" label-width="120px">
        <el-form-item label="企业" prop="enterpriseId" v-if="!isEdit">
          <el-select v-model="applicationForm.enterpriseId" placeholder="请选择企业" filterable>
            <el-option v-for="enterprise in enterprises" :key="enterprise.id" :label="enterprise.name"
              :value="enterprise.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="变更类型" prop="changeType">
          <el-select v-model="applicationForm.changeType" placeholder="请选择变更类型">
            <el-option label="企业名称变更" value="NAME_CHANGE"></el-option>
            <el-option label="法人变更" value="LEGAL_REPRESENTATIVE_CHANGE"></el-option>
            <el-option label="注册资本变更" value="REGISTERED_CAPITAL_CHANGE"></el-option>
            <el-option label="经营范围变更" value="BUSINESS_SCOPE_CHANGE"></el-option>
            <el-option label="地址变更" value="ADDRESS_CHANGE"></el-option>
            <el-option label="其他变更" value="OTHER"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="变更内容" prop="changeContent">
          <el-input type="textarea" v-model="applicationForm.changeContent" placeholder="请详细描述变更内容"
            :rows="4"></el-input>
        </el-form-item>
        <el-form-item label="申请原因" prop="reason">
          <el-input type="textarea" v-model="applicationForm.reason" placeholder="请说明申请原因" :rows="3"></el-input>
        </el-form-item>
        <el-form-item label="附件">
          <el-upload :action="uploadConfig.action" :headers="uploadConfig.headers" :file-list="fileList"
            :on-success="handleFileSuccess" :on-remove="handleFileRemove" multiple>
            <el-button size="small" type="primary">点击上传</el-button>
            <div slot="tip" class="el-upload__tip">支持上传多个文件，单个文件大小不超过10MB</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">提交申请</el-button>
      </span>
    </el-dialog>

    <!-- 申请详情对话框 -->
    <el-dialog title="申请详情" :visible.sync="detailDialogVisible" width="800px">
      <div v-if="currentApplication">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="申请编号">{{ currentApplication.applicationNumber }}</el-descriptions-item>
          <el-descriptions-item label="企业名称">{{ currentApplication.enterpriseName }}</el-descriptions-item>
          <el-descriptions-item label="变更类型">{{ getChangeTypeText(currentApplication.changeType)
            }}</el-descriptions-item>
          <el-descriptions-item label="申请状态">
            <el-tag :type="getStatusType(currentApplication.status)">
              {{ getStatusText(currentApplication.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ formatDateTime(currentApplication.submitDate) }}</el-descriptions-item>
          <el-descriptions-item label="处理时间" v-if="currentApplication.processDate">{{
            formatDateTime(currentApplication.processDate) }}</el-descriptions-item>
        </el-descriptions>

        <div style="margin-top: 20px;">
          <h4>变更内容</h4>
          <p>{{ currentApplication.changeContent }}</p>
        </div>

        <div style="margin-top: 20px;">
          <h4>申请原因</h4>
          <p>{{ currentApplication.reason }}</p>
        </div>

        <div v-if="currentApplication.processRemarks" style="margin-top: 20px;">
          <h4>处理备注</h4>
          <p>{{ currentApplication.processRemarks }}</p>
        </div>

        <div v-if="currentApplication.attachmentUrls && currentApplication.attachmentUrls.length > 0"
          style="margin-top: 20px;">
          <h4>附件</h4>
          <div v-for="(url, index) in currentApplication.attachmentUrls" :key="index">
            <el-link :href="url" target="_blank">附件{{ index + 1 }}</el-link>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 审批对话框 -->
    <el-dialog title="审批申请" :visible.sync="approvalDialogVisible" width="600px">
      <el-form :model="approvalForm" :rules="approvalRules" ref="approvalForm" label-width="100px">
        <el-form-item label="审批结果" prop="approved">
          <el-radio-group v-model="approvalForm.approved">
            <el-radio :label="true">批准</el-radio>
            <el-radio :label="false">拒绝</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="处理备注" prop="remarks">
          <el-input type="textarea" v-model="approvalForm.remarks" placeholder="请输入处理备注" :rows="4"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="approvalDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleApprovalSubmit" :loading="approving">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'BusinessChangeApplication',
  data() {
    return {
      applications: [],
      enterprises: [],
      loading: false,
      submitting: false,
      approving: false,
      dialogVisible: false,
      detailDialogVisible: false,
      approvalDialogVisible: false,
      dialogTitle: '新建申请',
      isEdit: false,
      currentApplication: null,
      fileList: [],

      searchForm: {
        applicationNumber: '',
        changeType: '',
        status: '',
        keyword: ''
      },

      pagination: {
        page: 1,
        size: 20,
        total: 0
      },

      applicationForm: {
        id: null,
        enterpriseId: null,
        changeType: '',
        changeContent: '',
        reason: '',
        attachmentUrls: []
      },

      approvalForm: {
        approved: true,
        remarks: ''
      },

      rules: {
        enterpriseId: [
          { required: true, message: '请选择企业', trigger: 'change' }
        ],
        changeType: [
          { required: true, message: '请选择变更类型', trigger: 'change' }
        ],
        changeContent: [
          { required: true, message: '请输入变更内容', trigger: 'blur' }
        ],
        reason: [
          { required: true, message: '请输入申请原因', trigger: 'blur' }
        ]
      },

      approvalRules: {
        remarks: [
          { required: true, message: '请输入处理备注', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    uploadConfig() {
      return this.$http.getUploadConfig()
    },
    canApprove() {
      // 根据用户角色判断是否可以审批
      const userRole = localStorage.getItem('userRole')
      return userRole === 'ADMIN' || userRole === 'SUPER_ADMIN'
    }
  },
  mounted() {
    this.loadApplications()
    this.loadEnterprises()
  },
  methods: {
    async loadApplications() {
      try {
        this.loading = true
        const params = {
          ...this.searchForm
        }
        const response = await this.$http.get('/enterprise-management/business-changes', { params })
        if (response.success) {
          const allData = response.data || []
          this.pagination.total = response.total || allData.length || 0
          
          if (response.total && response.total > allData.length) {
            this.applications = allData
          } else {
            const start = (this.pagination.page - 1) * this.pagination.size
            const end = this.pagination.page * this.pagination.size
            this.applications = allData.slice(start, end)
          }
        } else {
          this.$message.error('加载申请列表失败: ' + response.message)
        }
      } catch (error) {
        console.error('加载申请列表失败:', error)
        this.$message.error('加载申请列表失败')
      } finally {
        this.loading = false
      }
    },

    async loadEnterprises() {
      try {
        const response = await this.$http.get('/enterprise-management/enterprises')
        if (response.success) {
          this.enterprises = response.data || []
        }
      } catch (error) {
        console.error('加载企业列表失败:', error)
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.loadApplications()
    },

    handleReset() {
      this.searchForm = {
        applicationNumber: '',
        changeType: '',
        status: '',
        keyword: ''
      }
      this.pagination.page = 1
      this.loadApplications()
    },

    handleSizeChange(size) {
      this.pagination.size = size
      this.loadApplications()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadApplications()
    },

    handleAdd() {
      this.isEdit = false
      this.dialogTitle = '新建申请'
      this.dialogVisible = true
    },

    handleEdit(row) {
      this.isEdit = true
      this.dialogTitle = '编辑申请'
      this.applicationForm = {
        id: row.id,
        enterpriseId: row.enterpriseId,
        changeType: row.changeType,
        changeContent: row.changeContent,
        reason: row.reason,
        attachmentUrls: row.attachmentUrls || []
      }
      this.fileList = (row.attachmentUrls || []).map((url, index) => ({
        name: `附件${index + 1}`,
        url: url
      }))
      this.dialogVisible = true
    },

    handleView(row) {
      this.currentApplication = row
      this.detailDialogVisible = true
    },

    handleApprove(row) {
      this.currentApplication = row
      this.approvalForm = {
        approved: true,
        remarks: ''
      }
      this.approvalDialogVisible = true
    },

    handleFileSuccess(response) {
      if (response.success) {
        this.applicationForm.attachmentUrls.push(response.data.fileUrl)
      } else {
        this.$message.error('文件上传失败: ' + response.message)
      }
    },

    handleFileRemove(file) {
      if (file.response && file.response.success) {
        const index = this.applicationForm.attachmentUrls.indexOf(file.response.data.fileUrl)
        if (index > -1) {
          this.applicationForm.attachmentUrls.splice(index, 1)
        }
      }
    },

    async handleSubmit() {
      this.$refs.applicationForm.validate(async (valid) => {
        if (!valid) return false

        try {
          this.submitting = true
          let response
          if (this.isEdit) {
            response = await this.$http.put(`/enterprise-management/business-changes/${this.applicationForm.id}`, this.applicationForm)
          } else {
            response = await this.$http.post('/enterprise-management/business-changes', this.applicationForm)
          }

          if (response.success) {
            this.$message.success(this.isEdit ? '申请更新成功' : '申请提交成功')
            this.dialogVisible = false
            this.loadApplications()
          } else {
            this.$message.error('保存失败: ' + response.message)
          }
        } catch (error) {
          console.error('保存申请失败:', error)
          this.$message.error('保存申请失败')
        } finally {
          this.submitting = false
        }
      })
    },

    async handleApprovalSubmit() {
      this.$refs.approvalForm.validate(async (valid) => {
        if (!valid) return false

        try {
          this.approving = true
          const response = await this.$http.put(`/enterprise-management/business-changes/${this.currentApplication.id}/approve`, {
            approved: this.approvalForm.approved,
            processRemarks: this.approvalForm.remarks
          })

          if (response.success) {
            this.$message.success('审批完成')
            this.approvalDialogVisible = false
            this.loadApplications()
          } else {
            this.$message.error('审批失败: ' + response.message)
          }
        } catch (error) {
          console.error('审批失败:', error)
          this.$message.error('审批失败')
        } finally {
          this.approving = false
        }
      })
    },

    handleDialogClose() {
      this.$refs.applicationForm.resetFields()
      this.applicationForm = {
        id: null,
        enterpriseId: null,
        changeType: '',
        changeContent: '',
        reason: '',
        attachmentUrls: []
      }
      this.fileList = []
    },

    getChangeTypeText(type) {
      const types = {
        'NAME_CHANGE': '企业名称变更',
        'LEGAL_REPRESENTATIVE_CHANGE': '法人变更',
        'REGISTERED_CAPITAL_CHANGE': '注册资本变更',
        'BUSINESS_SCOPE_CHANGE': '经营范围变更',
        'ADDRESS_CHANGE': '地址变更',
        'OTHER': '其他变更'
      }
      return types[type] || type
    },

    getStatusText(status) {
      const statuses = {
        'PENDING': '待审核',
        'APPROVED': '已批准',
        'REJECTED': '已拒绝'
      }
      return statuses[status] || status
    },

    getStatusType(status) {
      const types = {
        'PENDING': 'warning',
        'APPROVED': 'success',
        'REJECTED': 'danger'
      }
      return types[status] || 'info'
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      return new Date(dateTime).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped lang="scss">
.business-change-application {
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 24px;
      color: #333;
    }
  }

  .search-card {
    margin-bottom: 20px;
  }
}

@media screen and (max-width: 768px) {
  .business-change-application {
    .header {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;

      h2 {
        font-size: 20px;
      }

      button {
        width: 100%;
      }
    }
  }
}
</style>